import ImageLightboxGallery from '@/components/media/ImageLightboxGallery'
import ImageLightboxPreviewTrigger from '@/components/media/ImageLightboxPreviewTrigger'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DetailWell } from '@/components/patterns/detail-page'
import { Typography } from '@/components/ui/typography'
import { MessageInspirationSourceType } from '@/lib/enums/MessageInspirationSourceType'
import { ExternalLink, Images } from 'lucide-react'
import { getMessageInspirationPreviewEntries } from './messageInspirationPreviewEntries'

function getSourceLabel(sourceType) {
  switch (sourceType) {
    case MessageInspirationSourceType.Upload.Value:
      return 'Direct upload'
    case MessageInspirationSourceType.PinterestBoard.Value:
      return 'Pinterest board'
    case MessageInspirationSourceType.GooglePhotosAlbum.Value:
      return 'Google Photos album'
    case MessageInspirationSourceType.IcloudAlbum.Value:
      return 'iCloud shared album'
    default:
      return 'Inspiration'
  }
}

export function hasMessageInspirationContent(message) {
  const sourceUrl = message?.inspiration_source_url || ''
  const previewEntries = getMessageInspirationPreviewEntries(message?.inspiration_media)

  return Boolean(sourceUrl) || previewEntries.length > 0
}

export default function MessageInspirationPanel({ message }) {
  const sourceType = message?.inspiration_source_type || null
  const sourceUrl = message?.inspiration_source_url || ''
  const previewEntries = getMessageInspirationPreviewEntries(message?.inspiration_media)

  if (!hasMessageInspirationContent(message)) {
    return null
  }

  const previewItems = previewEntries.map((entry) => entry.previewItem)

  return (
    <DetailWell className="space-y-4">
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex items-center gap-2">
            <Images className="size-5 text-muted-foreground" />
            <Typography variant="eyebrow">Inspiration</Typography>
          </div>
          <Badge variant="secondary">{getSourceLabel(sourceType)}</Badge>
        </div>
      </div>
      <div className="space-y-4">
        {sourceUrl ? (
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-3">
              <a
                href={sourceUrl}
                target="_blank"
                rel="noreferrer"
                className="min-w-0 flex-1 truncate text-sm text-interaction underline-offset-4 hover:underline"
              >
                {sourceUrl}
              </a>
              <Button asChild type="button" size="sm" variant="outline">
                <a href={sourceUrl} target="_blank" rel="noreferrer">
                  <ExternalLink />
                  Open shared album
                </a>
              </Button>
            </div>
          </div>
        ) : null}

        {previewItems.length > 0 ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <Typography variant="label">Uploaded photos</Typography>
              <Typography variant="supporting">
                {previewItems.length} image{previewItems.length === 1 ? '' : 's'}
              </Typography>
            </div>
            <ImageLightboxGallery items={previewItems}>
              {({ getTriggerProps }) => (
                <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                  {previewEntries.map((entry, index) => (
                    <ImageLightboxPreviewTrigger
                      key={entry.media.id ?? `${entry.media.url}-${index}`}
                      media={entry.media}
                      alt={entry.alt}
                      caption={entry.caption}
                      triggerProps={getTriggerProps(index)}
                      frameClassName="aspect-square w-full rounded-xl border border-border/70 bg-muted/30"
                      imageClassName="h-full w-full object-cover"
                    />
                  ))}
                </div>
              )}
            </ImageLightboxGallery>
          </div>
        ) : null}
      </div>
    </DetailWell>
  )
}
